'use client'

import { useRef } from 'react'
import WinTile from '../WinTile'
import { ArrowDownNarrowWide, ArrowDownWideNarrow, Loader2Icon } from 'lucide-react'

import { RevealSection } from '@/components/RevealSection'
import { Button } from '@/components/ui/button'
import { Pagination } from '@/components/ui/pagination'
import { WINS_PER_PAGE } from '@/constants/winsPerPage'
import { useLenisContext } from '@/providers/Lenis'
import { useWinsContext } from '@/providers/WinsProvider'
import { cn } from '@/utilities/ui'

/**
 * Displays wins in a paginated grid layout.
 * Data is paginated at source (server / API); only the current page is loaded.
 * Page and filter state (including reset-to-1 on filter change) are owned by WinsContext.
 */
const WinsGrid = () => {
  const {
    filteredWins,
    totalDocs,
    page,
    setPage,
    isListLoading,
    isSearching,
    searchTerm,
    sortDirection,
    toggleSortDirection,
  } = useWinsContext()
  const { lenis } = useLenisContext()

  const totalPages = Math.max(1, Math.ceil(totalDocs / WINS_PER_PAGE))
  const startIndex = totalDocs === 0 ? 0 : (page - 1) * WINS_PER_PAGE
  const endIndex = Math.min(startIndex + filteredWins.length, totalDocs)
  const isLoading = isSearching || isListLoading
  const gridRef = useRef<HTMLDivElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  const handlePageChange = (newPage: number) => {
    setPage(Math.max(1, Math.min(newPage, totalPages)))
    const container = containerRef.current
    if (container) {
      lenis?.scrollTo(container, { offset: 0 })
      gridRef.current?.focus()
    }
  }

  return (
    <>
      <div className="flex flex-col gap-4 lg:min-h-200" ref={containerRef}>
        <RevealSection stagger className="flex grow-0 items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <p className="font-body text-base font-normal text-foreground">
              {isLoading
                ? 'Loading...'
                : `Showing ${endIndex} of ${totalDocs} win${totalDocs !== 1 ? 's' : ''}`}
            </p>
            {isLoading && <Loader2Icon className="size-4 animate-spin text-muted-foreground" />}
          </div>

          <div className="flex items-center justify-end gap-2">
            <p className="p-0 text-sm">Sort by date</p>
            <Button
              variant="icon"
              ghost
              onClick={toggleSortDirection}
              className="p-0"
              aria-label="Sort by title"
            >
              {sortDirection === 'asc' ? (
                <ArrowDownNarrowWide className="size-5" />
              ) : (
                <ArrowDownWideNarrow className="size-5" />
              )}
            </Button>
          </div>
        </RevealSection>

        <RevealSection stagger threshold={0.001}>
          <div
            ref={gridRef}
            tabIndex={-1}
            className={cn(
              'flex flex-1 flex-wrap items-start justify-start gap-4 sm:grid sm:auto-rows-max sm:grid-cols-2 sm:items-stretch [&>.win-tile]:w-full [&>.win-tile]:max-w-full [&>.win-tile]:min-w-[306px]',
              isLoading && 'pointer-events-none opacity-50',
            )}
          >
            {filteredWins.map((win) => (
              <WinTile key={win.id} win={win} />
            ))}
            {!isLoading && totalDocs === 0 && searchTerm && (
              <p className="col-span-2 text-muted-foreground">
                No wins found matching &quot;{searchTerm}&quot;
              </p>
            )}
          </div>
        </RevealSection>
      </div>
      <RevealSection>
        <Pagination
          numResults={totalDocs}
          resultsPerPage={WINS_PER_PAGE}
          currentPage={page}
          onPageChange={handlePageChange}
        />
      </RevealSection>
    </>
  )
}

export default WinsGrid
